import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ImagePlus, Loader2, Save, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);
const inputCls = "bg-[#0C0A09] border-[#292524] text-stone-100";

const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
};

function SlideRow({ slide, onSaved, onDeleted }) {
  const [form, setForm] = useState(slide);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const replace = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("upload");
    try {
      const url = await uploadImage(file);
      const { data } = await api.put(`/admin/slides/${slide.id}`, { ...form, image: url });
      setForm(data);
      onSaved(data);
      toast.success("Imagem substituída!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar a imagem.");
    } finally {
      setBusy("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put(`/admin/slides/${slide.id}`, form);
      onSaved(data);
      toast.success("Textos salvos!");
    } catch {
      toast.error("Erro ao salvar.");
    } finally {
      setBusy("");
    }
  };

  const remove = async () => {
    if (!window.confirm("Remover esta imagem do carrossel?")) return;
    try {
      await api.delete(`/admin/slides/${slide.id}`);
      onDeleted(slide.id);
      toast.success("Imagem removida.");
    } catch {
      toast.error("Erro ao remover.");
    }
  };

  return (
    <div className="bg-[#1C1917] border border-[#292524] rounded-xl p-4 flex flex-col sm:flex-row gap-4" data-testid={`slide-row-${slide.id}`}>
      <div className="sm:w-56 shrink-0">
        <img src={imgUrl(form.image)} alt={form.title} className="w-full h-32 object-cover rounded-lg" data-testid={`slide-img-${slide.id}`} />
        <input ref={fileRef} type="file" accept="image/*" onChange={replace} className="hidden" data-testid={`slide-file-${slide.id}`} />
        <button onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid={`slide-replace-btn-${slide.id}`}
          className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-[#292524] py-1.5 text-xs hover:bg-[#292524] transition-colors disabled:opacity-60">
          {busy === "upload" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {busy === "upload" ? "Enviando..." : "Substituir imagem"}
        </button>
      </div>
      <div className="flex-1 space-y-2">
        <div><Label className="text-stone-300">Frase pequena</Label><Input value={form.eyebrow} onChange={set("eyebrow")} className={inputCls} data-testid={`slide-eyebrow-${slide.id}`} /></div>
        <div><Label className="text-stone-300">Título</Label><Input value={form.title} onChange={set("title")} className={inputCls} data-testid={`slide-title-${slide.id}`} /></div>
        <div><Label className="text-stone-300">Subtítulo</Label><Input value={form.sub} onChange={set("sub")} className={inputCls} data-testid={`slide-sub-${slide.id}`} /></div>
        <div className="flex gap-2 pt-1">
          <button onClick={save} disabled={!!busy} data-testid={`slide-save-btn-${slide.id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60">
            <Save className="w-4 h-4" /> {busy === "save" ? "Salvando..." : "Salvar textos"}
          </button>
          <button onClick={remove} data-testid={`slide-delete-btn-${slide.id}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#292524] px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors">
            <Trash2 className="w-4 h-4" /> Remover
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SlideManager() {
  const [slides, setSlides] = useState([]);
  const [adding, setAdding] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/slides").then(({ data }) => setSlides(data)).catch(() => {});
  }, []);

  const add = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAdding(true);
    try {
      const url = await uploadImage(file);
      const { data } = await api.post("/admin/slides", { image: url, eyebrow: "", title: "", sub: "" });
      setSlides((s) => [...s, data]);
      toast.success("Imagem adicionada ao carrossel!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar a imagem.");
    } finally {
      setAdding(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="max-w-3xl" data-testid="slide-manager">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-display font-black text-2xl">Carrossel da página inicial</h2>
          <p className="text-sm text-stone-500">Envie, substitua ou remova as imagens e edite os textos de cada slide.</p>
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={add} className="hidden" data-testid="slide-add-file" />
        <button onClick={() => fileRef.current?.click()} disabled={adding} data-testid="slide-add-btn"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
          {adding ? "Enviando..." : "Adicionar imagem"}
        </button>
      </div>
      <div className="space-y-3">
        {slides.length === 0 && <p className="text-sm text-stone-500" data-testid="slides-empty">Nenhuma imagem. O carrossel fica oculto no site.</p>}
        {slides.map((s) => (
          <SlideRow key={s.id} slide={s}
            onSaved={(d) => setSlides((all) => all.map((x) => (x.id === d.id ? d : x)))}
            onDeleted={(id) => setSlides((all) => all.filter((x) => x.id !== id))} />
        ))}
      </div>
    </div>
  );
}
